import React from 'react';
import { Typography, Box, Tooltip, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import config from 'src/config';
import { AuthenticatedImage, LabelChip } from 'src/components';
import { urlJoin } from 'src/utils/util';
import { componentConfig } from 'src/utils/natureStateIndicatorsHelper';
import { formatNumber } from 'src/utils/formatters';
import { AspectCategoryStatusIcon } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { getConcernLevel, getHighestImpactCategory } from 'src/utils/aspectCategoriesUtils';


const useStyles = makeStyles(theme => ({
  container: {
    width: '70%',
  },
  title: {
    fontWeight: 400,
    marginLeft: theme.spacing(2),
    fontSize: ({ isSite }) => isSite ? '2rem' : '2.3rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  textContainer: {
    flex: 1,
    minWidth: 0, // Permite que el contenido se comprima
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  smallAvatar: {
    width: '24px',
    height: '24px',
    fontSize: '1em',
    fontWeight: '500',
    border: '1px solid',
    borderColor: theme.palette.common.gray,
    backgroundColor: 'transparent',
    color: theme.palette.common.gray700,
  },
}));

const ProjectCard = props => {
  const {
    id: projectId,
    interestAreaThumbnailName,
    name,
    indicators = {},
    sites,
    isSite = false,
  } = props;

  const classes = useStyles({ isSite });

  const imageUrl = interestAreaThumbnailName
    ? urlJoin(config.serverUrl, 'api', 'folder', projectId, 'thumbnail', interestAreaThumbnailName)
    : undefined;

  const getAvailableIndicators = () => {
    if (!indicators || typeof indicators !== 'object') {
      return [];
    }

    const availableIndicators = [];

    // Recorrer todas las configuraciones de componentes
    Object.entries(componentConfig).forEach(([ , config ]) => {
      config.fields.forEach(field => {
        if (indicators[field] !== undefined && indicators[field] !== null) {
          const indicator = config.indicators[field];
          if (indicator) {
            availableIndicators.push({
              shortName: indicator.shortName,
              fullName: indicator.fullName,
              value: indicators[field],
              field,
            });
          }
        }
      });
    });

    return availableIndicators;
  };

  const availableIndicators = getAvailableIndicators();

  const getHighestConcernLevel = () => {
    if (availableIndicators.length === 0) {
      return null;
    }
    const concernLevels = availableIndicators.map(({ value }) => getConcernLevel(value));
    return getHighestImpactCategory(concernLevels);
  };

  const highestConcern = getHighestConcernLevel();

  const commonGroupBoxProps = {
    display: 'flex',
    height: isSite ? 28 : 30,
    alignItems: 'center',
    className: classes.group,
  };

  return (
    <Box display="flex" alignItems="center" flexGrow={1} alignContent="center" className={classes.container}>
      <AuthenticatedImage url={imageUrl} alt={`Imagen del proyecto ${name}`} width={isSite ? 120 : 140} height={isSite ? 120 : 140} />
      <Box display="flex" flexDirection="column" alignContent="center" className={classes.textContainer}>
        <Box display="flex" alignItems="center">
          <Tooltip title={name} placement="top-start">
            <Typography className={classes.title} variant="h4" color="textPrimary">
              {name}
            </Typography>
          </Tooltip>
        </Box>
        {availableIndicators.length > 0 &&
          <Box display="flex" alignItems="center" mt={0.5} ml={2}>
            <Box {...commonGroupBoxProps} ml={0.5}>
              <Box pl={0.5} pr={1}>
                <Typography variant="caption" color="textSecondary">Indicadores</Typography>
              </Box>
              <Divider orientation="vertical" />
              <Box pl={1} pr={1}>
                <AspectCategoryStatusIcon category={highestConcern} size="small" />
              </Box>
              <Divider orientation="vertical" />
              <Box ml={1} display="flex" alignItems="center" lineHeight={1}>
                {availableIndicators.map(({ shortName, fullName, value, field }) => (
                  <Tooltip key={field} title={fullName} arrow>
                    <Box mx={0.5} component="span">
                      <LabelChip
                        label={`${shortName} | ${formatNumber(value, 2)}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
            {!isSite &&
              <Box {...commonGroupBoxProps} alignContent="center" ml={0.5}>
                <Box mx={0.5}>
                  <Typography variant="caption" color="textSecondary">
                    Sitios
                  </Typography>
                </Box>
                <Divider orientation="vertical" />
                <Box ml={1} mr={1}>
                  {sites?.length || 0}
                </Box>
              </Box>
            }
          </Box>
        }
      </Box>
    </Box>
  );
};

ProjectCard.propTypes = {
  id: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  name: PropTypes.string,
  interestAreaThumbnailName: PropTypes.string,
  indicators: PropTypes.object,
  sites: PropTypes.array,
  isSite: PropTypes.bool,
};


export { ProjectCard };